import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { ElementRenderer } from "./element-renderer"
import { Button } from "../components/ui/button"
import { cn } from "../lib/utils"
import { resolveIcon } from "./resolve-icon"

export function ButtonRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent, itemContext, mode } = useRenderContext()
  const text = element.text ? String(resolveSymbol(element.text) ?? "") : undefined
  // Check if we have children (from rehype pipeline: button → span → text)
  const hasChildren = !text && element.children && element.children.length > 0

  // Resolve dynamic icon names: #{state.isPlaying ? 'pause' : 'play'} → "pause"
  const rawIcon = element.icon ?? ""
  const iconName = rawIcon.includes("#{")
    ? String(resolveSymbol(rawIcon) ?? "")
    : rawIcon
  const IconComponent = iconName ? resolveIcon(iconName) : undefined

  const variantMap: Record<string, "default" | "destructive" | "outline" | "secondary" | "ghost" | "link"> = {
    default: "default",
    destructive: "destructive",
    outline: "outline",
    secondary: "secondary",
    ghost: "ghost",
    link: "link",
  }

  const sizeMap: Record<string, "default" | "sm" | "lg" | "icon"> = {
    default: "default",
    sm: "sm",
    lg: "lg",
    icon: "icon",
    "icon-sm": "icon",
  }

  // When className contains explicit bg-* colors, use "ghost" variant to prevent
  // shadcn's default blue (bg-primary) from overriding the custom background
  const hasExplicitBg = element.className && /\bbg-(?!transparent|inherit|current)/.test(element.className)
  // Resolve dynamic variant expressions like #{ui.selectedDateRange === '1month' ? 'default' : 'outline'}
  const rawVariant = element.variant
    ? (element.variant.includes("#{") ? String(resolveSymbol(element.variant) ?? "") : element.variant)
    : ""
  const variant = rawVariant
    ? variantMap[rawVariant] ?? "default"
    : hasExplicitBg ? "ghost" : "default"
  const size = element.size ? sizeMap[element.size] ?? "default" : "default"

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault()
    if (mode !== "edit") e.stopPropagation()
    // Always dispatch tap — both inline events and external bindings (events.cogo.json) are handled
    // by the event dispatcher. itemContext enables iteration variable resolution (e.g., #{product.id})
    dispatchEvent(element.id, "tap", itemContext)
  }

  const extraClasses = cn(
    element.size === "icon-sm" && "h-8 w-8",
    element.fill && "w-full",
    element.className,
  )

  const iconEl = IconComponent ? <IconComponent className={element.iconClassName ?? "h-4 w-4"} /> : null
  const iconRight = element.iconPosition === "right"

  // Render children as content when no direct text (from rehype pipeline)
  const childContent = hasChildren
    ? element.children!.map((child, idx) => (
        <ElementRenderer key={child.id ?? `child-${idx}`} element={child} />
      ))
    : null

  // Link button: wrap in <a> if href is provided
  if (element.href) {
    const href = String(resolveSymbol(String(element.href)) ?? "")
    const target = element.target as string | undefined
    const rel = element.rel as string | undefined ?? (target === "_blank" ? "noopener noreferrer" : undefined)
    return (
      <a href={href} target={target} rel={rel}>
        <Button
          variant={variant}
          size={size}
          className={extraClasses || undefined}
        >
          {!iconRight && iconEl}
          {childContent ?? text}
          {iconRight && iconEl}
        </Button>
      </a>
    )
  }

  return (
    <Button
      variant={variant}
      size={size}
      className={extraClasses || undefined}
      onClick={handleClick}
    >
      {!iconRight && iconEl}
      {childContent ?? text}
      {iconRight && iconEl}
    </Button>
  )
}
